"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.footprintZips = exports.generateUrlForMlsListing = exports.formatListPriceHistoryItem = exports.formatHomevestStatusHistoryItem = exports.formatStatusHistoryItem = exports.getHomevestStatus = exports.getDisplayLine = exports.formatHomejunctionProperty = exports.formatOperationsProperty = exports.getImages = exports.updateProperty = exports.createPropertyFromListing = exports.castProviderId = exports.isListingAllowed = exports.updateHomejunctionProperty = exports.overrideNewPropertyStatus = exports.createHomejunctionProperty = exports.createOperationsProperty = exports.getConfigFromReview = exports.removedStatuses = exports.protectedHomevestStatuses = exports.homevestStatus = exports.inactiveStatus = exports.activeStatus = void 0;
const uuid_by_string_1 = __importDefault(require("uuid-by-string"));
const uuid_1 = require("uuid");
const short_uuid_1 = __importDefault(require("./short-uuid"));
const moment_timezone_1 = __importDefault(require("moment-timezone"));
const lodash_1 = require("lodash");
const PROVIDERS = Object.freeze({
    HOMEJUNCTION: 'homejunction',
    MANUAL_UPLOAD: 'manual_upload'
});
const getDefaultConfig = (purchasePrice) => ({
    acquisition: {
        purchasePrice,
        improvementCost: 0,
        closingCostPercentage: 0.015
    },
    mortgage: {
        purchasePricePercentage: 0.6,
        interestRate: 0.045
    },
    expenses: {
        propertyTaxRate: 0.0116,
        insuranceRate: 0.0056,
        HOARate: 0,
        capexRate: 0.005
    },
    tenantTerms: {
        initialLeaseTerm: 24
    }
});
exports.activeStatus = 'Active';
exports.inactiveStatus = 'Inactive';
exports.homevestStatus = {
    pendingReview: 'pending_review',
    listed: 'listed',
    // Pre-purchased homes or off-market listings
    internallyListed: 'internally_listed',
    pendingApplication: 'pending_application',
    homevested: 'homevested',
    removed: 'removed',
    reviewerRejected: 'reviewer_rejected',
    demandTest: 'demand_test'
};
// Should not automatically update homevestStatus for
// properties with these statuses
exports.protectedHomevestStatuses = [
    exports.homevestStatus.internallyListed,
    exports.homevestStatus.pendingApplication,
    exports.homevestStatus.homevested,
    exports.homevestStatus.reviewerRejected,
    exports.homevestStatus.demandTest
];
exports.removedStatuses = [exports.homevestStatus.reviewerRejected, exports.homevestStatus.removed];
const getConfigFromReview = (property) => {
    const review = property.reviews[property.reviews.length - 1];
    const rent = Number(review.homevestRent);
    return {
        acquisition: {
            purchasePrice: Number(review.homevestPurchasePrice),
            improvementCost: Number(review.improvementCost),
            closingCostPercentage: Number(review.closingCostPercentage) / 100
        },
        mortgage: {
            purchasePricePercentage: Number(review.mortgagePurchasePricePercentage) / 100,
            interestRate: Number(review.interestRatePercentage) / 100
        },
        expenses: {
            propertyTaxRate: Number(review.propertyTaxRatePercentage) / 100,
            insuranceRate: Number(review.insuranceRatePercentage) / 100,
            HOARate: Number(review.HOARatePercentage) / 100,
            capexRate: Number(review.capexRatePercentage) / 100
        },
        tenantTerms: {
            rent,
            initialLeaseTerm: Number(review.initialLeaseTerm)
        }
    };
};
exports.getConfigFromReview = getConfigFromReview;
const createOperationsProperty = (operationsProperty) => {
    const property = (0, exports.formatOperationsProperty)(operationsProperty);
    if (!(0, lodash_1.get)(operationsProperty, 'address.stdAddress')) {
        throw new Error('Address standardization is required for a dark listing!');
    }
    property.id = (0, uuid_by_string_1.default)(operationsProperty.address.stdAddress);
    property.shortId = short_uuid_1.default.fromUUID(property.id);
    property.listPriceHistory = [(0, exports.formatListPriceHistoryItem)(property)];
    property.homevestStatusHistory = [(0, exports.formatHomevestStatusHistoryItem)(property)];
    property.statusHistory = [(0, exports.formatStatusHistoryItem)(property)];
    return property;
};
exports.createOperationsProperty = createOperationsProperty;
const createHomejunctionProperty = (homejunctionProperty, propertyOverrides = {}) => {
    const property = (0, exports.formatHomejunctionProperty)(homejunctionProperty, propertyOverrides);
    property.id = (0, uuid_1.v4)();
    property.shortId = short_uuid_1.default.fromUUID(property.id);
    property.listPriceHistory = [(0, exports.formatListPriceHistoryItem)(property)];
    property.homevestStatusHistory = [(0, exports.formatHomevestStatusHistoryItem)(property)];
    property.statusHistory = [(0, exports.formatStatusHistoryItem)(property)];
    return property;
};
exports.createHomejunctionProperty = createHomejunctionProperty;
// Has an admin selected an status override before our current time?
// If so we replace the status with the override
const overrideNewPropertyStatus = (previousProperty, newProperty) => {
    if (!previousProperty) {
        throw new Error('A previousProperty is required!');
    }
    if (!newProperty) {
        throw new Error('A newProperty is required!');
    }
    const { statusOverride, statusOverrideUntil } = previousProperty;
    newProperty = Object.assign({}, newProperty);
    if (statusOverride &&
        statusOverrideUntil &&
        (0, moment_timezone_1.default)(new Date(statusOverrideUntil.seconds * 1000)).isAfter(new Date())) {
        newProperty.status = statusOverride;
    }
    return newProperty;
};
exports.overrideNewPropertyStatus = overrideNewPropertyStatus;
const updateHomejunctionProperty = (previousProperty, homejunctionProperty, propertyOverrides = {}) => {
    return (0, exports.updateProperty)(previousProperty, homejunctionProperty, PROVIDERS.HOMEJUNCTION, propertyOverrides);
};
exports.updateHomejunctionProperty = updateHomejunctionProperty;
/**
 * Determine if the listing from a provider is allowed.
 *
 * @param {Object} listing - The listing object from the provider.
 * @param {Object} provider - The provider ID.
 *
 */
const isListingAllowed = (providerListing, provider) => {
    if (provider !== PROVIDERS.HOMEJUNCTION) {
        return true;
    }
    // HJ Specific rule, flush this out better when we have more restrictions.
    // Withheld addresses
    // https://slipstream.homejunction.com/#/ws/responses?id=withheld-address
    if (!(0, lodash_1.get)(providerListing, 'address.deliveryLine')) {
        console.warn('No deliveryLine passed, this is a withheld listing and will not be processed.');
        return false;
    }
    else {
        return true;
    }
};
exports.isListingAllowed = isListingAllowed;
const castProviderId = (listing, provider) => {
    if ([PROVIDERS.HOMEJUNCTION, PROVIDERS.MANUAL_UPLOAD].includes(provider)) {
        return listing.id;
    }
    else {
        throw new Error('Unknown provider passed to update MLS!');
    }
};
exports.castProviderId = castProviderId;
const createPropertyFromListing = (providerListing, provider) => {
    let newProperty;
    if (provider === PROVIDERS.HOMEJUNCTION) {
        newProperty = (0, exports.createHomejunctionProperty)(providerListing);
    }
    else if (provider === PROVIDERS.MANUAL_UPLOAD) {
        newProperty = (0, exports.createOperationsProperty)(providerListing);
    }
    else {
        throw new Error('Unknown provider passed to update MLS!');
    }
    return newProperty;
};
exports.createPropertyFromListing = createPropertyFromListing;
const updateProperty = (previousProperty, property, provider, propertyOverrides = {}) => {
    // If the property has been reviewed, default property overrides to
    // be from the review
    if ((0, lodash_1.get)(previousProperty, 'reviews.length')) {
        const configFromReview = (0, exports.getConfigFromReview)(previousProperty);
        propertyOverrides = (0, lodash_1.merge)({
            rent: configFromReview.tenantTerms.rent,
            config: configFromReview
        }, propertyOverrides);
    }
    // Pass homevestStatus as an override bc current homevestStatus
    // can inform future homevestStatus
    propertyOverrides.homevestStatus = previousProperty.homevestStatus;
    const initialNewProperty = (0, exports.overrideNewPropertyStatus)(previousProperty, property);
    let formattedProperty;
    if (provider === PROVIDERS.HOMEJUNCTION) {
        formattedProperty = (0, exports.formatHomejunctionProperty)(initialNewProperty, propertyOverrides);
    }
    else if (provider === PROVIDERS.MANUAL_UPLOAD) {
        formattedProperty = (0, exports.formatOperationsProperty)(initialNewProperty);
    }
    else {
        throw new Error(`Unknown provider ${provider} to update property!`);
    }
    const newProperty = Object.assign({}, previousProperty, formattedProperty);
    if (previousProperty.images && newProperty.images) {
        newProperty.images = (0, exports.getImages)(previousProperty, newProperty);
    }
    // Update the listPriceHistory if the listPrice has changed
    if (previousProperty.listPrice !== newProperty.listPrice) {
        newProperty.listPriceHistory = [(0, exports.formatListPriceHistoryItem)(newProperty)].concat(previousProperty.listPriceHistory || []);
    }
    if (previousProperty.status !== newProperty.status) {
        newProperty.statusHistory = [(0, exports.formatStatusHistoryItem)(newProperty)].concat(newProperty.statusHistory || []);
    }
    if (previousProperty.homevestStatus !== newProperty.homevestStatus) {
        newProperty.homevestStatusHistory = [(0, exports.formatHomevestStatusHistoryItem)(newProperty)].concat(newProperty.homevestStatusHistory || []);
    }
    return newProperty;
};
exports.updateProperty = updateProperty;
/**
 * This is a temp fix to prevent updates to properties where we have uploaded images
 */
const getImages = (previousProperty, newProperty) => {
    const hasUploadedImages = (0, lodash_1.get)(previousProperty, 'images[0]', '').startsWith('https://storage');
    return hasUploadedImages ? previousProperty.images : newProperty.images;
};
exports.getImages = getImages;
const formatOperationsProperty = (homejunctionProperty) => {
    const formattedProperty = Object.assign({}, homejunctionProperty);
    formattedProperty.homevestStatus =
        formattedProperty.homevestStatus || exports.homevestStatus.pendingReview;
    // Set provider id
    const providerId = (0, lodash_1.get)(homejunctionProperty, 'address.stdAddress');
    if (!providerId) {
        throw new Error('Manual upload properties require a standardized address as provider ID!');
    }
    formattedProperty.provider = 'manual_upload';
    formattedProperty.systemId = providerId;
    formattedProperty.providerId = providerId;
    // Add display lines to address
    const [displayLine1, displayLine2] = (0, exports.getDisplayLine)(homejunctionProperty.address);
    formattedProperty.address.displayLine1 = displayLine1;
    formattedProperty.address.displayLine2 = displayLine2;
    // Add weighted total to baths
    // This should be refactored
    if (typeof formattedProperty.baths === 'object') {
        formattedProperty.baths.weightedTotal =
            (formattedProperty.baths.full || 0) + (formattedProperty.baths.half || 0) / 2;
    }
    return formattedProperty;
};
exports.formatOperationsProperty = formatOperationsProperty;
const formatHomejunctionProperty = (homejunctionProperty, propertyOverrides = {}) => {
    const formattedProperty = Object.assign({}, homejunctionProperty);
    formattedProperty.provider = 'homejunction';
    formattedProperty.providerId = homejunctionProperty.id;
    // Add weighted total to baths
    if (typeof formattedProperty.baths === 'object') {
        formattedProperty.baths.weightedTotal =
            (formattedProperty.baths.full || 0) + (formattedProperty.baths.half || 0) / 2;
    }
    // Add display lines to address
    const [displayLine1, displayLine2] = (0, exports.getDisplayLine)(homejunctionProperty.address);
    formattedProperty.address.displayLine1 = displayLine1;
    formattedProperty.address.displayLine2 = displayLine2;
    delete formattedProperty.id;
    delete formattedProperty.daysOnHJI;
    delete formattedProperty.daysOnMarket;
    (0, lodash_1.merge)(formattedProperty, propertyOverrides);
    if (propertyOverrides.config) {
        formattedProperty.config = (0, lodash_1.merge)(getDefaultConfig(formattedProperty.listPrice), propertyOverrides.config);
    }
    if ((0, lodash_1.get)(formattedProperty, 'listingAgent.id')) {
        formattedProperty.listingAgent.id = String(formattedProperty.listingAgent.id).toUpperCase();
    }
    if ((0, lodash_1.get)(formattedProperty, 'coListingAgent.id')) {
        formattedProperty.coListingAgent.id = String(formattedProperty.coListingAgent.id).toUpperCase();
    }
    formattedProperty.homevestStatus = (0, exports.getHomevestStatus)(formattedProperty);
    return formattedProperty;
};
exports.formatHomejunctionProperty = formatHomejunctionProperty;
const getDisplayLine = (address) => {
    const displayLine1 = address.deliveryLine || address.street;
    let displayLine2 = '';
    if (address.city) {
        displayLine2 += `${address.city}, `;
    }
    if (address.state) {
        displayLine2 += `${address.state}`;
    }
    if (address.zip) {
        if (address.state)
            displayLine2 += ' ';
        displayLine2 += address.zip;
    }
    return [displayLine1, displayLine2];
};
exports.getDisplayLine = getDisplayLine;
const getHomevestStatus = (property) => {
    if (property.provider === 'homejunction') {
        return getHomejunctionPropertyHomevestStatus(property);
    }
    else {
        throw new Error(`Unknown provider: ${property.provider}`);
    }
};
exports.getHomevestStatus = getHomevestStatus;
const getHomejunctionPropertyHomevestStatus = (property) => {
    // Don't change protected statuses
    if (exports.protectedHomevestStatuses.includes(property.homevestStatus)) {
        return property.homevestStatus;
    }
    else if (!property.inBuyBox && !(0, lodash_1.get)(property, 'reviews.length')) {
        return exports.homevestStatus.removed;
    }
    else if (property.status !== exports.activeStatus &&
        (property.homevestStatus === exports.homevestStatus.listed ||
            property.homevestStatus === exports.homevestStatus.pendingReview)) {
        return exports.homevestStatus.removed;
        // If the property is active, update according to required listing criteria
    }
    else if (property.status === exports.activeStatus) {
        return property.rent && property.config && (0, lodash_1.get)(property, 'images.length')
            ? exports.homevestStatus.listed
            : exports.homevestStatus.pendingReview;
    }
    else {
        return property.homevestStatus;
    }
};
const formatStatusHistoryItem = (property) => {
    return {
        status: property.status,
        dataPulledAt: new Date()
    };
};
exports.formatStatusHistoryItem = formatStatusHistoryItem;
const formatHomevestStatusHistoryItem = (property) => {
    return {
        homevestStatus: property.homevestStatus,
        dataPulledAt: new Date()
    };
};
exports.formatHomevestStatusHistoryItem = formatHomevestStatusHistoryItem;
const formatListPriceHistoryItem = (property) => {
    const history = {
        listPrice: property.listPrice,
        dataPulledAt: new Date()
    };
    if (property.listingAgent) {
        history.listingAgent = property.listingAgent;
    }
    if (property.listingOffice) {
        history.listingOffice = property.listingOffice;
    }
    return history;
};
exports.formatListPriceHistoryItem = formatListPriceHistoryItem;
const generateUrlForMlsListing = (mlsListing) => {
    if (!mlsListing.region || !mlsListing.id) {
        throw new Error('A region and ID are required for a URL!');
    }
    return `https://app.upandup.co/homes/${encodeURIComponent(mlsListing.region)}/${encodeURIComponent(mlsListing.id)}`;
};
exports.generateUrlForMlsListing = generateUrlForMlsListing;
exports.footprintZips = [
    '63116',
    '63109',
    '63139',
    '63143',
    '63119',
    '63132',
    '63130',
    '63123',
    '63111',
    '63117',
    '63144',
    '63110'
];
